import { storageService } from "@/lib/storage";
import { isJwtSessionEnded } from "@/lib/jwtSession";
import type { AuthEmployee, AuthResponse } from "@/features/auth/types";
import { logger } from "@/shared/logger";

const VAULT_STORAGE_PREFIX = "auth.offline_vault.v1.";

/** Lower iterations in tests so unit tests stay fast. */
export const PBKDF2_ITERATIONS =
  import.meta.env.MODE === "test" ? 1_000 : 120_000;

export function normalizePhoneForVault(phone: string): string {
  return phone.trim().toLowerCase();
}

type VaultPayload = {
  access_token: string;
  employee: AuthEmployee;
};

type StoredVaultRecord = {
  v: 1;
  saltB64: string;
  ivB64: string;
  ciphertextB64: string;
};

function uint8ToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

function base64ToUint8(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}

function asArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength
  ) as ArrayBuffer;
}

function vaultStorageKey(normalizedPhone: string): string {
  return `${VAULT_STORAGE_PREFIX}${normalizedPhone}`;
}

async function deriveAesKey(
  normalizedPhone: string,
  pin: string,
  salt: Uint8Array
): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const passwordBytes = enc.encode(`${normalizedPhone}\0${pin}`);
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    passwordBytes,
    { name: "PBKDF2" },
    false,
    ["deriveBits", "deriveKey"]
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: asArrayBuffer(salt),
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

export async function saveOfflineSessionVault(
  whatsappNumber: string,
  pin: string,
  payload: VaultPayload
): Promise<void> {
  logger.info("offlineVault.save.start", {
    phone: whatsappNumber,
    employeeId: payload.employee?.id,
    hasAccessToken: !!payload.access_token,
  });
  const normalized = normalizePhoneForVault(whatsappNumber);
  const enc = new TextEncoder();
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const aesKey = await deriveAesKey(normalized, pin, salt);
  const plaintext = enc.encode(JSON.stringify(payload));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: asArrayBuffer(iv) },
    aesKey,
    asArrayBuffer(plaintext)
  );
  const record: StoredVaultRecord = {
    v: 1,
    saltB64: uint8ToBase64(salt),
    ivB64: uint8ToBase64(iv),
    ciphertextB64: uint8ToBase64(new Uint8Array(ciphertext)),
  };
  await storageService.set(vaultStorageKey(normalized), record);
  logger.info("offlineVault.save.success", {
    phone: whatsappNumber,
    employeeId: payload.employee?.id,
  });
}

export async function removeOfflineSessionVault(whatsappNumber: string): Promise<void> {
  const normalized = normalizePhoneForVault(whatsappNumber);
  await storageService.remove(vaultStorageKey(normalized));
  logger.info("offlineVault.remove", { phone: whatsappNumber });
}

export async function tryUnlockOfflineSession(
  whatsappNumber: string,
  pin: string
): Promise<AuthResponse | null> {
  logger.info("offlineVault.unlock.start", { phone: whatsappNumber });
  const normalized = normalizePhoneForVault(whatsappNumber);
  const record = await storageService.get<StoredVaultRecord>(vaultStorageKey(normalized));
  if (!record || record.v !== 1 || !record.saltB64 || !record.ivB64 || !record.ciphertextB64) {
    logger.warn("offlineVault.unlock.noRecord", { phone: whatsappNumber });
    return null;
  }
  try {
    const salt = base64ToUint8(record.saltB64);
    const iv = base64ToUint8(record.ivB64);
    const ciphertext = base64ToUint8(record.ciphertextB64);
    const aesKey = await deriveAesKey(normalized, pin, salt);
    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: asArrayBuffer(iv) },
      aesKey,
      asArrayBuffer(ciphertext)
    );
    const json = JSON.parse(new TextDecoder().decode(decrypted)) as VaultPayload;
    if (!json?.access_token || !json?.employee) return null;
    if (isJwtSessionEnded(json.access_token)) {
      logger.warn("offlineVault.unlock.tokenEnded", { phone: whatsappNumber });
      return null;
    }
    logger.info("offlineVault.unlock.success", {
      phone: whatsappNumber,
      employeeId: json.employee.id,
    });
    return {
      access_token: json.access_token,
      expires_in: 0,
      employee: json.employee,
      device_trusted: true,
    };
  } catch (error) {
    logger.warn("offlineVault.unlock.failed", { phone: whatsappNumber, error });
    return null;
  }
}
